import { useState, useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Trash2 } from 'lucide-react';
import type { FieldIssue } from '../../types';
import type { ValidationFeedback } from '@/api/campaigns';
import {
  CharCount,
  FieldLabel,
  InlineIssue,
  Field,
  UploadZone,
  inputClass,
  issueBorder,
} from './shared';

// Point pdfjs at the worker bundled by Vite
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

// ─── Constants ────────────────────────────────────────────────────────────────

const INTRO_LIMIT = 150;
const HEADLINE_LIMIT = 70;

// ─── Main component ───────────────────────────────────────────────────────────

interface LinkedInDocumentDetailsProps {
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  caption: string;
  onCaptionChange: (v: string) => void;
  headline: string;
  onHeadlineChange: (v: string) => void;
  onFileChange: (f: File | null) => void;
  onPageImageChange: (url: string | null) => void;
  onUrlChange: (v: string) => void;
  onLegalChange: (v: string) => void;
  initialValues?: { url?: string; legal?: string };
  initialPdfUrl?: string;
  initialPageImageUrl?: string;
  onExistingPdfClear?: () => void;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
}

export function LinkedInDocumentDetails({
  showErrors,
  showWarnings,
  fieldIssues,
  caption,
  onCaptionChange,
  headline,
  onHeadlineChange,
  onFileChange,
  onPageImageChange,
  onUrlChange,
  onLegalChange,
  initialValues,
  initialPdfUrl,
  initialPageImageUrl,
  onExistingPdfClear,
  feedback,
  feedbackPending,
  onAIFeedback,
}: LinkedInDocumentDetailsProps) {
  // Pre-seed from the parent's cached render (same-session remount) so there's no flash of
  // empty state after save. Falls back to null (triggering the pdfjs useEffect) on page refresh.
  const [pageImageUrl, setPageImageUrl] = useState<string | null>(initialPageImageUrl ?? null);
  // Track whether the current thumbnail was loaded from an existing DB asset (vs a new upload).
  // Used to call onExistingPdfClear when the user removes it so the parent can queue deletion.
  const isShowingExistingRef = useRef(!!initialPdfUrl);
  const [destinationUrl, setDestinationUrl] = useState(initialValues?.url ?? '');
  const [legal, setLegal] = useState(initialValues?.legal ?? '');

  useEffect(() => {
    onUrlChange(initialValues?.url ?? '');
    onLegalChange(initialValues?.legal ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount, if an existing PDF asset URL is provided (page refresh case — no cached render),
  // fetch and render page 1 via pdfjs. Skipped if initialPageImageUrl is already available
  // (same-session remount — the parent's cached render is used directly via useState above).
  useEffect(() => {
    if (initialPageImageUrl || !initialPdfUrl) return;
    (async () => {
      try {
        const resp = await fetch(initialPdfUrl);
        const arrayBuffer = await resp.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvas, viewport }).promise;
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setPageImageUrl(dataUrl);
        onPageImageChange(dataUrl);
      } catch {
        /* silent — just don't show thumbnail */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(f: File) {
    isShowingExistingRef.current = false;
    onFileChange(f);

    // Auto-populate headline from filename if not already set
    if (!headline) {
      const name = f.name.replace(/\.pdf$/i, '');
      onHeadlineChange(name.slice(0, HEADLINE_LIMIT));
    }

    // Render first page to a data URL for preview + mockup
    try {
      const arrayBuffer = await f.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvas, viewport }).promise;
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      setPageImageUrl(dataUrl);
      onPageImageChange(dataUrl);
    } catch {
      setPageImageUrl(null);
      onPageImageChange(null);
    }
  }

  function removeFile() {
    if (isShowingExistingRef.current) {
      onExistingPdfClear?.();
      isShowingExistingRef.current = false;
    }
    setPageImageUrl(null);
    onFileChange(null);
    onPageImageChange(null);
  }

  return (
    <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0">
      {/* ── About ───────────────────────────────────────────────────── */}
      <div className="border-l-2 border-[#0014cc]/30 pl-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
          About this format
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          A <span className="font-medium text-slate-700">PDF document</span> users can swipe through
          in-feed on LinkedIn. The headline appears in the blue title bar above the document
          preview. Users can download the full document.{' '}
          <a
            href="https://business.linkedin.com/advertise/ads/sponsored-content/document-ads/specs"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
          >
            LinkedIn Document Ad specs ↗
          </a>
        </p>
      </div>

      {/* ── PDF Upload ──────────────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
          PDF Document
        </p>
        <div className="border-b border-slate-200 mb-3" />

        {pageImageUrl ? (
          // First-page thumbnail — same h-36 pattern as AssetPreview
          <div className="h-36 flex items-center justify-center">
            <div className="relative h-full aspect-[3/4] group/img">
              <img
                src={pageImageUrl}
                alt="Page 1 preview"
                className="h-full w-full object-cover rounded-lg border border-slate-200"
              />
              <button
                type="button"
                onClick={removeFile}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md opacity-0 group-hover/img:opacity-100 transition-opacity"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          <>
            <UploadZone
              onFile={(f) => {
                if (f.type === 'application/pdf' || f.name.endsWith('.pdf')) handleFile(f);
              }}
              hint="Click to upload PDF"
              accept=".pdf,application/pdf"
              issueType={
                fieldIssues['asset_files']?.errors.length && showErrors
                  ? 'error'
                  : fieldIssues['asset_files']?.warnings.length && showWarnings
                    ? 'warning'
                    : undefined
              }
            />
            <InlineIssue
              issue={fieldIssues['asset_files']}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            />
          </>
        )}
      </div>

      {/* ── Primary Text ────────────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Primary Text"
          right={<CharCount value={caption} limit={INTRO_LIMIT} />}
          tooltip="Post body text shown above the document. Recommended 150 chars."
        />
        <textarea
          value={caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          placeholder="Write something that will make your audience stop scrolling..."
          rows={4}
          className={`${inputClass} resize-none ${issueBorder(fieldIssues['primary_text'], showErrors, showWarnings)}`}
        />
        <InlineIssue
          issue={fieldIssues['primary_text']}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── Headline ────────────────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Headline"
          right={<CharCount value={headline} limit={HEADLINE_LIMIT} />}
          tooltip="Shown in the blue document title bar. Max 70 chars."
        />
        <input
          type="text"
          value={headline}
          onChange={(e) => onHeadlineChange(e.target.value)}
          placeholder="Document title..."
          className={`${inputClass} ${issueBorder(fieldIssues['headline'], showErrors, showWarnings)}`}
        />
        <InlineIssue
          issue={fieldIssues['headline']}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── Destination URL ──────────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Destination URL"
          tooltip="Landing page URL shown when users click through."
        />
        <input
          type="url"
          value={destinationUrl}
          onChange={(e) => {
            setDestinationUrl(e.target.value);
            onUrlChange(e.target.value);
          }}
          placeholder="https://www.ford.com/..."
          className={`${inputClass} ${issueBorder(fieldIssues['destination_url'], showErrors, showWarnings)}`}
        />
        <InlineIssue
          issue={fieldIssues['destination_url']}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── Legal ───────────────────────────────────────────────────── */}
      <Field
        label="Legal"
        tooltip="Required in some markets (e.g. France) for regulated categories. Internal only — not displayed in the mockup."
        fieldKey="legal"
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
      >
        <textarea
          value={legal}
          onChange={(e) => {
            setLegal(e.target.value);
            onLegalChange(e.target.value);
          }}
          placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
          rows={5}
          className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
        />
      </Field>
    </div>
  );
}
